import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ref, reactive, computed, watch, PropType } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'

import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { IgnoredListService, IgnoredTerminalListReq, IgnoredTerminalItem } from './ignored-list.service'
import { TerminalStatus } from '../discovered-list/discovered-list.service'
import { Discover, TerminalDiscoveryService } from '../../terminal-discovery.service'
import { ElMessage, ElMessageBox } from 'element-plus'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import { TerminalTypeOptions } from '@/views/dashboard/_enum/terminal-type'
import { formatTimeStamp } from '@/utils/time'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'


export default /*#__PURE__*/_defineComponent({
  __name: 'ignored-list',
  props: {
  hasPermission: {
    type: Boolean,
    default: false
  },
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  }
},
  emits: ['viewAll'],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t } = useI18n()
const props = __props

const emits = __emit

const injector = useReflectiveInjector([IgnoredListService, TerminalDiscoveryService])
const service = injector.get(IgnoredListService)
const terminalDiscoveryService = injector.get(TerminalDiscoveryService)

const tableRef = ref<any>()
const { refreshList } = useCmTableListRefresh(tableRef)
const remarkDialogVisible = ref(false)
const remarkText = ref('')
const currentRow = ref<IgnoredTerminalItem | null>(null)
const totalCount = computed(() => tableRef.value?.total || 0)
const selectedIds = ref<string[]>([])

const form = reactive<IgnoredTerminalListReq & { keyword?: string }>({
  keyword: '',
  system_type: undefined,
  status: TerminalStatus.ignored,
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20
})

watch(
  () => props.viewMode,
  (vm) => {
    form.limit = vm === TableViewMode.preview ? 10 : 20
  }
)

const formColumns = reactive<ObjectFormItem<typeof form, 'system_type'>>({
  system_type: {
    colOpt: { style: 'width: 150px;flex:0 0 auto' },
    hiddenAllLabel: true,
    outSearchFiledFlag: true,
    label: t('discoveredList.osPlaceholder'),
    type: FormType.Select,
    selectOpt: {
      clearable: true,
    },
    dictData: TerminalTypeOptions,
    events: {
      change: handleQuery,
    },
  }
})

const pageOption = {
  pageNumName: 'page',
  pageSizeName: 'limit'
}

const tableOpt = {
  bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : undefined,
  columns: [
    { prop: 'ip_address', label: t('discoveredList.ipAddress'),minWidth:150, columnOpt: { sortable: true } },
    { prop: 'mac', label: t('discoveredList.macAddress'),minWidth:150, },
    { prop: 'computer_name', label: t('discoveredList.computerName'),minWidth:150,showOverflow: true, formatter: (_row: any, value: string) => value || '--' },
    { prop: 'operating_system', label: t('discoveredList.osVersion'),minWidth:200, showOverflow: true, formatter: (_row: any, value: string) => (value === 'Unknown' || !value) ? '--' : value },
    { prop: 'hardware_info', label: t('discoveredList.vendorInfo'),minWidth:200, showOverflow: true, formatter: (_row: any, value: string) => (value === 'Unknown' || !value) ? '--' : value },
    { prop: 'subnet_segment', label: t('discoveredList.subnetSegment'), minWidth:150,showOverflow: true,},
    { prop: 'first_time', label: t('discoveredList.firstFoundTime'),minWidth:150, formatter: (_row: any, value: number) => formatTimeStamp(value, 'YYYY-MM-DD HH:mm') },
    { prop: 'last_time', label: t('discoveredList.lastFoundTime'),minWidth:150, formatter: (_row: any, value: number) => formatTimeStamp(value, 'YYYY-MM-DD HH:mm') },
    { prop: 'remarks', label: t('discoveredList.remark'), showOverflow: true, columnOpt: { minWidth: 140 } }
  ],
  rowKey: 'id',
  showActionBar: false,
  showSearch: false,
  showRightToolbar: true,
  selection: true,
  height: props.viewMode === TableViewMode.preview ? undefined : '100%',
  path: 'list',
  totalPath: 'total',
  usePagination: false,
  disableScrollLoad: props.viewMode === TableViewMode.preview,
  scrollLoadThreshold: 10,
  scrollLoadDelay: 100
}

const tableActOpt = {
  columnOpt: { label: t('commonPage.operation'), width: 80, fixed: 'right' },
  actionButtons: () => [
    { name: t('discoveredList.remark'), type: 'remark', opt: { size: 'small', type: 'text' }, disabled: () => !props.hasPermission }
  ]
}

function getListApi(params: any) {
  // 字段名映射：表格列 prop -> API 字段名
  const sortFieldMap: Record<string, string> = {
    'ip_address': 'ip'
  }
  const apiSort = params.sort ? (sortFieldMap[params.sort] || params.sort) : 'ip'
  return service.getIgnoredTerminalList({
    ...params,
    limit: params.limit || (props.viewMode === TableViewMode.preview ? 10 : 20),
    page: params.page || 1,
    // 如果用户没有手动排序，使用默认排序
    sort: apiSort,
    asc: params.asc !== undefined ? params.asc : true
  })
}

function handleViewAll() {
  emits('viewAll')
}

function handleSelectionChange(selection: any[]) {
  selectedIds.value = selection.map((item: any) => item.mac)
}

function handleBatchCancelIgnore() {
  ElMessageBox.confirm(t('ignoredList.confirmCancelIgnore', { count: selectedIds.value.length }), t('cmtable.tip')).then(() => {
    terminalDiscoveryService.ignoreTerminal({ macs: selectedIds.value, status: Discover.discovered }).then(() => {
      ElMessage.success(t('ignoredList.cancelIgnoreSuccess'))
      selectedIds.value = []
      refreshData()
    })
  }).catch(() => { })
}

function handleAction({ type, rowItem }: { type: string, rowItem: any }) {
  if (type === 'remark') {
    handleRemark(rowItem)
  }
}

function handleRemark(row: any) {
  currentRow.value = row
  remarkText.value = row.remarks || ''
  remarkDialogVisible.value = true
}

function handleSaveRemark() {
  if (!currentRow.value) return
  service.updateTerminalRemark({
    id: currentRow.value.id,
    remarks: remarkText.value
  }).then(() => {
    ElMessage.success(t('discoveredList.remarkSaved'))
    remarkDialogVisible.value = false
    refreshData()
  })
}

async function handleSearch(value: string) {
  form.keyword = value
  await refreshList()
}

async function handleQuery() {
  await refreshList()
}

function handleReset() {
  form.keyword = ''
  form.system_type = undefined
  form.page = 1
  void refreshList()
}

function refreshData() {
  void refreshList()
}

const exportQueryParams = computed<IgnoredTerminalListReq>(() => ({
  ...form,
  limit: 0,
  page: 0
}))

function download(type: 0 | 1) {
  service.download(type, exportQueryParams.value, t('ignoredList.exportFileName'))
}

__expose({
  refreshData
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt,
      "table-act-opt": tableActOpt,
      api: getListApi,
      "has-permission": __props.hasPermission,
      modelValue: form,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form) = $event)),
      "page-option": pageOption,
      onHandleAction: handleAction,
      onChangeSelected: handleSelectionChange
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          modelValue: form,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form) = $event)),
          "form-columns": formColumns,
          disabled: !__props.hasPermission,
          "show-search": true,
          "search-placeholder": _ctx.$t('ignoredList.searchPlaceholder'),
          "popover-threshold": 0,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          actions: _withCtx(() => [
            _createVNode(_component_el_button, {
              type: "primary",
              size: "small",
              onClick: handleBatchCancelIgnore,
              disabled: selectedIds.value.length === 0 || !__props.hasPermission
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('ignoredList.cancelIgnore')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(ExportDropdown, {
              "has-permission": __props.hasPermission,
              onExport: download
            }, null, 8, ["has-permission"]),
            (props.viewMode === _unref(TableViewMode).preview)
              ? (_openBlock(), _createBlock(_component_el_button, {
                  key: 0,
                  type: "primary",
                  size: "small",
                  plain: "",
                  onClick: handleViewAll,
                  disabled: !__props.hasPermission
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('commonPage.viewAll')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"])
      ]),
      _: 1
    }, 8, ["has-permission", "modelValue"]),
    _createVNode(_component_el_dialog, {
      modelValue: remarkDialogVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((remarkDialogVisible).value = $event)),
      title: _ctx.$t('discoveredList.remark'),
      width: "500px",
      "destroy-on-close": ""
    }, {
      footer: _withCtx(() => [
        _createVNode(_component_el_button, {
          size: "small",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (remarkDialogVisible.value = false))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_el_button, {
          size: "small",
          type: "primary",
          onClick: handleSaveRemark
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.confirm')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_input, {
          modelValue: remarkText.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((remarkText).value = $event)),
          type: "textarea",
          rows: 4,
          placeholder: _ctx.$t('discoveredList.pleaseEnterRemark'),
          maxlength: "500",
          "show-word-limit": ""
        }, null, 8, ["modelValue", "placeholder"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ], 64))
}
}

})